import React from "react";
import {
  X,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Activity,
  Brain,
  Zap,
  ShieldCheck,
} from "lucide-react";

export default function WhyThisWorksModal({ open, onClose, onBook }) {
  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        backgroundColor: "rgba(0,0,0,0.9)",
        backdropFilter: "blur(10px)",
        animation: "fadeIn 0.2s ease-out",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "900px",
          height: "90vh",
          background: "#0b0c10",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "24px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 50px 100px -20px rgba(0,0,0,0.9)",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "24px 32px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "rgba(11, 12, 16, 0.95)",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontFamily: "monospace",
              color: "var(--brand)",
              letterSpacing: "1px",
            }}
          >
            // THE OPERATING SYSTEM
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* SCROLLABLE BODY */}
        <div style={{ overflowY: "auto", padding: "40px", flex: 1 }}>
          {/* 1. HERO: THE FRAME */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "60px",
              maxWidth: "700px",
              margin: "0 auto 60px",
            }}
          >
            <h2
              style={{
                fontSize: "36px",
                fontWeight: "800",
                lineHeight: "1.1",
                color: "#fff",
                marginBottom: "16px",
              }}
            >
              Most fitness fails because
              <br />
              the plan fights your life.
            </h2>
            {/* UPDATED COPY: "Consistency" instead of "Compliance" */}
            <p
              style={{
                fontSize: "18px",
                color: "#9ca3af",
                lineHeight: "1.5",
                marginBottom: "32px",
              }}
            >
              Simpler wins by designing for <strong>consistency</strong> first,
              then intensity.
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "24px",
                flexWrap: "wrap",
              }}
            >
              {[
                "15-min blocks → You actually do it",
                "Pain-aware → No flare ups",
                "Weekly adjustments → No guessing",
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                    color: "#fff",
                    fontWeight: "500",
                  }}
                >
                  <CheckCircle2 size={16} color="var(--brand)" /> {item}
                </div>
              ))}
            </div>
          </div>

          {/* 2. THE VISUAL: TWO LANES (Old vs New) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
              marginBottom: "60px",
            }}
          >
            {/* LEFT LANE: OLD WAY */}
            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: "16px",
                padding: "32px",
                opacity: 0.7,
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#ef4444",
                  fontWeight: "800",
                  textTransform: "uppercase",
                  marginBottom: "16px",
                  letterSpacing: "1px",
                }}
              >
                The Old Model
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#fff",
                  marginBottom: "24px",
                }}
              >
                Motivation-Based
              </div>

              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {[
                  "Big complex plan",
                  "Busy week happens",
                  "Miss sessions (Guilt)",
                  "Add intensity to 'catch up'",
                  "Pain / Injury / Burnout",
                  "Quit & Restart",
                ].map((item, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      color: "#9ca3af",
                      fontSize: "14px",
                    }}
                  >
                    <XCircle size={14} color="#ef4444" /> {item}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  marginTop: "24px",
                  fontSize: "13px",
                  fontStyle: "italic",
                  color: "#666",
                }}
              >
                Relies on willpower.
              </div>
            </div>

            {/* RIGHT LANE: SIMPLER WAY */}
            <div
              style={{
                background: "rgba(34,197,94,0.05)",
                border: "1px solid rgba(34,197,94,0.3)",
                borderRadius: "16px",
                padding: "32px",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-10px",
                  right: "20px",
                  background: "var(--brand)",
                  color: "#000",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "800",
                }}
              >
                WINNER
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--brand)",
                  fontWeight: "800",
                  textTransform: "uppercase",
                  marginBottom: "16px",
                  letterSpacing: "1px",
                }}
              >
                The Simpler Model
              </div>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#fff",
                  marginBottom: "24px",
                }}
              >
                System-Based
              </div>

              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {[
                  "Small daily standard",
                  "Pain-aware guardrails",
                  "Quick wins in Week 1",
                  "Weekly Coach Adjustment",
                  "Repeatable Routine",
                  "Results that stick",
                ].map((item, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    <CheckCircle2 size={14} color="var(--brand)" /> {item}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  marginTop: "24px",
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "var(--brand)",
                }}
              >
                Relies on a system.
              </div>
            </div>
          </div>

          {/* INTRIGUE INSERT - UPDATED COPY */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "60px",
              padding: "0 20px",
            }}
          >
            <p
              style={{
                fontSize: "18px",
                color: "#fff",
                fontStyle: "italic",
                lineHeight: "1.6",
              }}
            >
              "Most people don’t need more discipline. They need fewer
              decisions.
              <br />
              <span style={{ color: "var(--brand)" }}>
                The moment we remove friction, consistency becomes automatic.
              </span>
              "
            </p>
          </div>

          {/* 3. PROOF CARDS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "60px",
            }}
          >
            {[
              {
                label: "Week-1 Adherence",
                val: "88%",
                sub: "Clients complete first 7 days.",
              },
              {
                label: "Pain-Free Sessions",
                val: "92%",
                sub: "Reported no increase in pain.",
              },
              {
                label: "Protein Target Hit",
                val: "+24g",
                sub: "Average increase in 2 weeks.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="glass"
                style={{ padding: "24px", textAlign: "center" }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    textTransform: "uppercase",
                    color: "#9ca3af",
                    marginBottom: "8px",
                  }}
                >
                  {card.label}
                </div>
                <div
                  style={{
                    fontSize: "32px",
                    fontWeight: "800",
                    color: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {card.val}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {card.sub}
                </div>
              </div>
            ))}
          </div>

          {/* 4. THE METHOD (Diagnose -> Decide -> Deploy) */}
          <div style={{ marginBottom: "60px" }}>
            <h3
              style={{
                textAlign: "center",
                color: "#fff",
                marginBottom: "32px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              The 3-Step Method
            </h3>
            <div style={{ display: "grid", gap: "16px" }}>
              {[
                {
                  icon: <Activity size={20} />,
                  title: "1. Diagnose",
                  text: "We find the bottleneck. Movement, recovery, or stress.",
                  quote: "No guessing.",
                },
                {
                  icon: <Brain size={20} />,
                  title: "2. Decide",
                  text: "We pick one lever. The week has one single priority.",
                  quote: "One lever at a time.",
                },
                {
                  icon: <Zap size={20} />,
                  title: "3. Deploy",
                  text: "We adjust the plan to match your life. Not the other way around.",
                  quote: "If it isn’t repeatable, it isn’t real.",
                },
              ].map((step, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    padding: "20px",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "12px",
                    borderLeft: "4px solid var(--brand)",
                  }}
                >
                  <div style={{ color: "var(--brand)" }}>{step.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        color: "#fff",
                        fontWeight: "700",
                        fontSize: "16px",
                      }}
                    >
                      {step.title}
                    </div>
                    <div style={{ color: "#9ca3af", fontSize: "14px" }}>
                      {step.text}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontStyle: "italic",
                      color: "#555",
                      display: "none",
                      "@media (min-width: 600px)": { display: "block" },
                    }}
                  >
                    "{step.quote}"
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 5. PRIZE FRAME */}
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              borderRadius: "24px",
              padding: "40px",
              textAlign: "center",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                marginBottom: "16px",
                color: "var(--brand)",
                fontWeight: "800",
                fontSize: "12px",
                letterSpacing: "1px",
              }}
            >
              <ShieldCheck size={16} /> QUALIFICATION
            </div>
            <h3
              style={{
                fontSize: "28px",
                fontWeight: "800",
                color: "#fff",
                marginBottom: "16px",
              }}
            >
              This isn't for everyone.
            </h3>
            <p
              style={{
                color: "#9ca3af",
                maxWidth: "500px",
                margin: "0 auto 32px",
                fontSize: "15px",
                lineHeight: "1.6",
              }}
            >
              It’s for people who want a standard, not a hype speech. If you
              miss workouts because life is busy, you’re exactly who this is
              built for.
            </p>
            <button
              onClick={() => {
                onClose();
                onBook();
              }}
              className="btn primary"
              style={{ padding: "16px 32px", fontSize: "16px" }}
            >
              Book a Consult <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
